export type Point = {
  x: number;
  y: number;
};

/**
 * Maps a value from one scale to another.
 * @param x value to map
 * @param sMin source min value
 * @param sMax source max value
 * @param tMin target min value
 * @param tMax target max value
 */
export function mapNumber(
  x: number,
  sMin: number,
  sMax: number,
  tMin: number,
  tMax: number
) {
  if (sMax === 0 && sMin === 0) return tMin;
  return tMin + (tMax - tMin) * ((x - sMin) / (sMax - sMin)) || 0;
}

/**
 * Returns the length of a vector
 * @param {Point} point
 */
export function vectorLength(point: Point) {
  return Math.sqrt(point.x ** 2 + point.y ** 2);
}

/**
 * Returns a new vector, scaled by the divisor
 * @param point
 * @param divisor
 */
export function vectorDiv(point: Point, divisor: number) {
  if (divisor == 0) {
    console.warn("trying to divide vector", { ...point }, "by zero");
    return;
  }
  return {
    x: point.x / divisor,
    y: point.y / divisor
  };
}

/**
 *
 * @param point
 * @param multiplier
 */
export function vectorMult(point: Point, multiplier: number) {
  return {
    x: point.x * multiplier,
    y: point.y * multiplier
  };
}

export function vectorNormalize(point: Point) {
  const length = vectorLength(point);
  if (length == 0) {
    return { x: 0, y: 0 };
  }

  return {
    x: point.x / length,
    y: point.y / length
  };
}

/**
 * Rounds a number up, always AWAY from zero
 * e.g. 0.1 => 1, -0.1 => -1
 */
export function roundUp(num: number) {
  return num < 0 ? Math.floor(num) : Math.ceil(num);
}

/**
 * Rounds a number down, always TOWARDS zero
 * e.g. 1.1 => 1, -1.1 => -1
 */
export function roundDown(num: number) {
  return num < 0 ? Math.ceil(num) : Math.floor(num);
}

/**
 * Clamps a number between a min and max value.
 * Allows for null values to be passed in.
 */
export function clampNumber(value: number, min?: number | null, max?: number | null) {
  if (min == null && max == null) return value;
  if (min == null) return Math.min(value, max || value);
  if (max == null) return Math.max(value, min);
  return Math.min(Math.max(value, min), max);
}